/**
 * Helpers for human-readable relative timestamps in the CMM order notes UI.
 */

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Returns a compact relative time string for note headers (e.g. "2 hours ago", "Yesterday").
 *
 * @param from - Past instant to describe relative to `now`.
 * @param now - Reference clock (defaults to current time; injectable for tests).
 * @returns User-facing label; uses "Just now" for very recent or future-ish times.
 */
export function formatRelativeTime(from: Date, now: Date = new Date()): string {
  const ms = now.getTime() - from.getTime();
  if (ms < MS_PER_MINUTE) return "Just now";

  const minutes = Math.floor(ms / MS_PER_MINUTE);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(ms / MS_PER_HOUR);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(ms / MS_PER_DAY);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;

  return from.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: from.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}
